import { AllowedDocumentsSet } from "models/documents/allowed-document";
import { PaxCheckChecklist, PaxCheckQuestion } from "../../api";
import ChecklistItem, {
  DocumentRequirement,
  InfoItem,
} from "./checklist-item/checklist-item";
import ChecklistItemStatus from "./checklist-item/checklist-item-status";
import ChecklistResolutionStatus, {
  ChecklistResolutionStatusFromApi,
} from "./checklist-resolution-status";
import ChecklistStatus, { ChecklistStatusFromApi } from "./checklist-status";

interface ChecklistProps {
  id: string;
  status: ChecklistStatus;
  resolutionStatus: ChecklistResolutionStatus;
  nationality?: string | null;
  hasNationalityQuestion: boolean;
  birthdate?: Date | null;
  hasBirthdateQuestion: boolean;
  questions: Array<PaxCheckQuestion>;
  items: Array<ChecklistItem>;
  allowedTravelDocumentsSet: AllowedDocumentsSet;
}

class Checklist {
  id: string;
  status: ChecklistStatus;
  resolutionStatus: ChecklistResolutionStatus;
  nationality?: string | null;
  hasNationalityQuestion: boolean;
  birthdate?: Date | null;
  hasBirthdateQuestion: boolean;
  questions: Array<PaxCheckQuestion>;
  items: Array<ChecklistItem>;
  allowedTravelDocumentsSet: AllowedDocumentsSet;

  constructor(p: ChecklistProps) {
    this.id = p.id;
    this.status = p.status;
    this.resolutionStatus = p.resolutionStatus;
    this.nationality = p.nationality;
    this.hasNationalityQuestion = p.hasNationalityQuestion;
    this.birthdate = p.birthdate;
    this.hasBirthdateQuestion = p.hasBirthdateQuestion;
    this.questions = p.questions;
    this.items = p.items;
    this.allowedTravelDocumentsSet = p.allowedTravelDocumentsSet;
  }

  static fromApi(c: PaxCheckChecklist): Checklist {
    return new Checklist({
      id: c.id,
      status: ChecklistStatusFromApi(c.status),
      resolutionStatus: ChecklistResolutionStatusFromApi(c.resolutionStatus),
      nationality: c.nationality,
      hasNationalityQuestion: c.hasNationalityQuestion,
      birthdate: c.birthdate,
      hasBirthdateQuestion: c.hasBirthdateQuestion,
      questions: (c.questions && c.questions.map((q) => q)) || [],
      allowedTravelDocumentsSet: AllowedDocumentsSet.fromApi(
        c.allowedTravelDocumentsSet
      ),
      items:
        (c.items &&
          c.items.map((item) => {
            if (item.type === "info-item") {
              return InfoItem.fromApi(item);
            }
            // else - document requirement
            return DocumentRequirement.fromApi(item);
          })) ||
        [],
    });
  }

  isFinalized(): boolean {
    return this.status === ChecklistStatus.Finalized;
  }

  /*
  Whether every document an item allows was already offered by a resolved item,
  leaving the pax nothing new to provide - eg a transit item asking again for
  the passport already scanned as travel doc. Such items are left for the
  checklist page instead of being walked through in the flow.

  Residency proof also allows the passport, but always alongside a residence
  permit, which is never a travel document - so a scanned travel doc can't
  cover its whole set and the item stays in the flow.
  */
  isCoveredByResolvedItems(item: ChecklistItem): boolean {
    if (item instanceof DocumentRequirement === false) {
      return false;
    }

    const allowedDocuments = item.allowedDocumentsSet.allowedDocuments;

    if (allowedDocuments.length === 0) {
      // api sends no allowed documents set - nothing to ask the pax for
      return true;
    }

    const seenDocTypes = this.items
      .filter(
        (it) =>
          it instanceof DocumentRequirement === true &&
          it.status === ChecklistItemStatus.Resolved
      )
      .flatMap((it) =>
        (it as DocumentRequirement).allowedDocumentsSet.allowedDocuments.map(
          (doc) => doc.type
        )
      );

    return allowedDocuments.every((doc) => seenDocTypes.includes(doc.type));
  }

  hasQuestions(): boolean {
    return (
      this.hasNationalityQuestion ||
      this.hasBirthdateQuestion ||
      this.questions?.length > 0
    );
  }

  hasQuestionsToAnswer(): boolean {
    if (
      (this.hasNationalityQuestion && this.nationality === undefined) ||
      (this.hasBirthdateQuestion && this.birthdate === undefined)
    ) {
      return true;
    }

    const questionsToAddress = this.questions;

    if (questionsToAddress.length === 0) {
      return false;
    }

    const notAnsweredQuestions = questionsToAddress.find(
      (q) => q.answer === undefined
    );

    if (notAnsweredQuestions === undefined) {
      return false;
    }

    return true;
  }

  getItemById(id: string | null | undefined): ChecklistItem | null {
    return this.items.find((item) => item.id === id) || null;
  }

  getItemsWithStatuses(
    statuses: Set<ChecklistItemStatus>
  ): Array<ChecklistItem> {
    return this.items.filter((item) => statuses.has(item.status));
  }

  totalItems(): number {
    return this.items.length;
  }
}

export default Checklist;
